import { put, takeEvery, select, all, take } from 'redux-saga/effects'

import { Game, Api, Auth } from '../../Core'

import Phase1Screen from './Phase1Screen'
import LastTurnPopup from './LastTurnPopup'
import NewSessionPopup from './NewSessionPopup'
import WaitingSessionStartPopup from './WaitingSessionStartPopup'

import * as actions from './phases.actions'
import * as authSelectors from '../../Core/Auth/auth.selectors'
import * as firebaseActions from '../../Core/Firebase/firebase.actions'
import * as firebaseSelectors from '../../Core/Firebase/firebase.selectors'
import * as battlesActions from '../Battles/battles.actions'
import * as indicatorsActions from '../Indicators/indicators.actions'
import * as loaderActions from '../../Core/Loader/loader.actions'
import * as loaderSelectors from '../../Core/Loader/loader.selectors'

import * as audioActions from '../../Core/Game/AudioContainer/audio.actions'
import * as mapActions from '../Map/map.actions'

import { getCurrentPhase } from './phases.selectors'
import { getSignInData } from '../../Core/Firebase/firebase.selectors'

import { PHASES, SCREENS } from './phases.const'

function* init() {
  yield loadComponents()
}

/**
 * Main loader screen
 */
function* mainLoaderScreen() {
  yield put(loaderActions.addLoader('phases_sagas_loader'))
  const [userInfoAction, firebaseInitAction, groupDataAction, playerListAction, userDataAction] =
    yield all([
      take(Auth.actions.USER_INFO_FINISH_SUCCESS),
      take(firebaseActions.FIREBASE_INITIALIZED),
      take(firebaseActions.SET_GROUP_DATA),
      take(firebaseActions.SET_PLAYER_LIST),
      take(indicatorsActions.SET_USER_DATA),
    ])
  yield put(
    actions.gameLoadSuccess({
      userInfoAction,
      firebaseInitAction,
      groupDataAction,
      playerListAction,
      userDataAction,
    })
  )
  const playerData = yield select(firebaseSelectors.getUserData)
  if (playerData.audioDisabled) {
    yield put(Game.actions.disableAudio(true))
  }
}

function* waitingSessionSuccess() {
  let signInData = yield select(getSignInData)
  signInData.playerData = signInData.userData
  yield initializePhase({ data: signInData })
  yield put(loaderActions.removeLoader('changing_session_loader'))
}

function* gameLoadSuccess({ data: { firebaseInitAction } }) {
  const activeLoaders = yield select(loaderSelectors.getActiveLoaders)
  if (Object.keys(activeLoaders).length < 2) {
    yield initializePhase(firebaseInitAction)
  }
  yield put(loaderActions.removeLoader('phases_sagas_loader'))
}

function* initializePhase(firebaseInitAction) {
  const {
    data: {
      playersList,
      uid,
      playerData: { phase },
    },
  } = firebaseInitAction

  if (phase == undefined) {
    Game.actions.startSceneChange('start_scene')
  }
  if (phase) {
    if (phase == PHASES.BUILD) {
      yield put(Game.actions.startSceneChange('map_scene'))
    }
    yield put(actions.goToPhase(phase))
  } else {
    /* legacy code, only useful for old games without phase in player data */
    const groupConfig = yield select(firebaseSelectors.getGroupData)

    const ownPlayerListData = playersList[uid]
    if (
      !ownPlayerListData ||
      !ownPlayerListData.colonyName ||
      !ownPlayerListData.colonyShield ||
      !ownPlayerListData.colonyShield.bannerGeometry ||
      (!ownPlayerListData.federationId && groupConfig.federationsType === 'manual')
    ) {
      yield put(Game.actions.removeScreen('phase1_screen'))
      yield put(Game.actions.addScreen('phase0_screen'))
    } else {
      yield put(actions.goToPhase(PHASES.INFORMATION))
    }
  }
}

/**
 * Load components
 */
export function* loadComponents() {
  const components = {
    Phase1Screen,
    LastTurnPopup,
    NewSessionPopup,
    WaitingSessionStartPopup,
  }

  yield put(Game.actions.registerComponents(components))
}

function* nextPhaseSagas() {
  const currentPhase = yield select(getCurrentPhase)
  const nextPhase = currentPhase + 1 > 3 ? 1 : currentPhase + 1
  yield put(actions.goToPhase(nextPhase))
  currentPhase ? yield put(audioActions.removeHowlAudio()) : null
  yield put(audioActions.playHowlAudio(nextPhase))
}

function* nextTurnSagas() {
  yield put(actions.goToPhase(PHASES.INFORMATION))
  yield put(audioActions.removeHowlAudio())
  yield put(audioActions.playHowlAudio(1))
}

function* goToPhaseSagas({ data }) {
  yield put(actions.loadPhaseScreens(data))

  yield saveCurrentPhase(data)

  yield put({ type: actions.GO_TO_PHASE_SUCCESS, data })
}

function* saveCurrentPhase(phase) {
  const {
    licenseData: { groupId, id: playerId },
  } = yield select(authSelectors.getUserInfo)
  const payload = {
    phase,
    groupId,
    playerId,
  }
  try {
    yield Api.put('/saveCurrentPhase', {}, payload)
  } catch (e) {
    console.log('Error saving phase', e)
    if (e && e.status === 401) {
      console.log('Unauthorized')
      yield put(Game.actions.returnToPlatform())
    }
  }
}

function* loadPhaseScreensSagas({ data }) {
  // clear all screens and popups
  yield put(Game.actions.clearAllPopups())

  yield put(
    mapActions.setVisitorMap({
      visitorId: null,
      mapId: null,
      buildings: {},
      removedRocks: {},
    })
  )

  // load screens from current phase
  switch (data) {
    case PHASES.CREATE_COLONY:
      yield put(Game.actions.setScreens(SCREENS.CREATE_COLONY))
      yield put(audioActions.removeHowlAudio())
      yield put(audioActions.playHowlAudio(0))
      break
    case PHASES.INFORMATION:
      yield put({ type: 'STOP_ANIMATIONS' })
      yield put(battlesActions.setIndividualBattle(null))
      yield put(battlesActions.getBattle())
      yield put(Game.actions.setScreens(SCREENS.INFORMATION))
      yield put(Game.actions.startSceneChange('start_scene'))
      yield put(audioActions.removeHowlAudio())
      yield put(audioActions.playHowlAudio(1))
      break
    case PHASES.MINE:
      yield put(Game.actions.startSceneChange('map_scene'))
      yield put(Game.actions.setScreens(SCREENS.BUILD))
      yield put(battlesActions.getBattle())
      yield put(battlesActions.getOwnBattle())
      yield put(audioActions.removeHowlAudio())
      yield put(audioActions.playHowlAudio(2))
      break
    case PHASES.BUILD:
      yield put(Game.actions.setScreens(SCREENS.BUILD))
      yield put(battlesActions.getBattle())
      yield put(battlesActions.getOwnBattle())
      yield put(audioActions.removeHowlAudio())
      yield put(audioActions.playHowlAudio(3))
      break
    default:
    // put some default screen here
  }
}
/**
 * Sagas entrypoint.
 */
export function* sagas() {
  yield all([
    init(),
    mainLoaderScreen(),
    takeEvery(Auth.actions.USER_INFO_FINISH_SUCCESS, loadPhaseScreensSagas),
    takeEvery(actions.NEXT_PHASE, nextPhaseSagas),
    takeEvery(actions.NEXT_TURN, nextTurnSagas),
    takeEvery(actions.GO_TO_PHASE, goToPhaseSagas),
    takeEvery(actions.LOAD_PHASE_SCREENS, loadPhaseScreensSagas),
    takeEvery(actions.GAME_LOAD_SUCCESS, gameLoadSuccess),
    takeEvery(actions.GAME_WAITING_SESSION_SUCCESS, waitingSessionSuccess),
  ])
}
